import React from 'react';
import { motion } from 'framer-motion';

// --- Reusable Atomic Shapes ---
const SkeletonLine = ({ width, className = "" }: { width: string; className?: string }) => (
    <div className={`h-2.5 rounded-full ${className}`} style={{ width }} />
);

const SkeletonCircle = ({ size, className = "" }: { size: string; className?: string }) => (
    <div className={`rounded-full ${className}`} style={{ width: size, height: size }} />
);

const SkeletonRect = ({ className = "" }: { className?: string }) => (
    <div className={`rounded-md ${className}`} />
);

// --- Card Container (Glassmorphic & Blue Tints) ---
const CardBase = ({ children, className = "", style = {} }: { children: React.ReactNode; className?: string; style?: any }) => (
    <div
        className={`relative backdrop-blur-xl bg-gradient-to-br from-white/90 to-white/50 border border-white/60 shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] rounded-2xl overflow-hidden ${className}`}
        style={style}
    >
        {/* Subtle inner overlay for tint */}
        <div className="absolute inset-0 bg-blue-50/20 pointer-events-none" />
        {children}
    </div>
);

// --- Organic Doodles ---
// --- Organic Doodles (Hand-drawn style) ---
const DoodleArrow = ({ className }: { className?: string }) => (
    <motion.svg
        className={className}
        viewBox="0 0 100 60"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{
            duration: 2,
            ease: "easeInOut",
            delay: 1,
            repeat: Infinity,
            repeatType: "reverse",
            repeatDelay: 1
        }}
    >
        {/* Curvy arrow pointing inward */}
        <path d="M10 50 Q 40 10, 80 30" />
        <path d="M80 30 L 70 20 M 80 30 L 75 40" />
    </motion.svg>
);

const DoodleCircle = ({ className }: { className?: string }) => (
    <motion.svg
        className={className}
        viewBox="0 0 100 100"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0, rotate: -10 }}
        animate={{ pathLength: 1, rotate: 10 }}
        transition={{
            pathLength: { duration: 2, ease: "easeInOut" },
            rotate: { duration: 10, repeat: Infinity, repeatType: "mirror" }
        }}
    >
        {/* Messy hand-drawn circle/oval */}
        <path d="M50 10 C 20 10 10 30 10 50 C 10 80 30 90 50 90 C 80 90 90 70 90 50 C 90 20 70 10 50 10 Z M 48 8 C 15 8 5 35 5 50" />
    </motion.svg>
);

const DoodleScribble = ({ className }: { className?: string }) => (
    <motion.svg
        className={className}
        viewBox="0 0 120 30"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{
            duration: 2.5,
            ease: "easeInOut",
            delay: 0.5,
            repeat: Infinity,
            repeatType: "reverse",
            repeatDelay: 0.5
        }}
    >
        {/* Loopy underline scribble */}
        <path d="M5 15 Q 15 5 25 15 T 45 15 T 65 15 T 85 15 T 105 15" />
    </motion.svg>
);



const DoodleHighlight = ({ className }: { className?: string }) => (
    <motion.svg
        className={className}
        viewBox="0 0 200 40"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{
            duration: 2,
            ease: "easeInOut",
            delay: 1.2,
            repeat: Infinity,
            repeatType: "reverse",
            repeatDelay: 3
        }}
    >
        {/* Zigzag/scribble highlight */}
        <path d="M10 20 Q 20 10, 30 20 T 50 20 T 70 20 T 90 20 T 110 20 T 130 20 T 150 20 T 170 20" />
    </motion.svg>
);

const DoodleBurst = ({ className }: { className?: string }) => (
    <motion.svg
        className={className}
        viewBox="0 0 100 100"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: [0, 1.2, 1], opacity: [0, 1, 0.8] }}
        transition={{
            duration: 2,
            ease: "backOut",
            delay: 1.5,
            repeat: Infinity,
            repeatType: "reverse",
            repeatDelay: 1
        }}
    >
        {/* Starburst/Idea lines */}
        <path d="M50 20 L 50 10 M 80 50 L 90 50 M 50 80 L 50 90 M 20 50 L 10 50" />
        <path d="M70 30 L 78 22 M 70 70 L 78 78 M 30 70 L 22 78 M 30 30 L 22 22" />
    </motion.svg>
);

// --- Specific Skeletons ---

// 1. Presentation Slide Skeleton
const PresentationSkeleton = ({ className = "" }: { className?: string }) => {
    return (
        <CardBase className={`w-72 h-48 p-5 flex flex-col gap-4 ${className}`}>
            {/* Header */}
            <div className="relative z-10 flex items-center gap-3 mb-1">
                <SkeletonCircle size="28px" className="bg-blue-200/50" />
                <div className="space-y-2">
                    <SkeletonLine width="100px" className="bg-blue-600/20" />
                    <SkeletonLine width="50px" className="bg-blue-400/20" />
                </div>
            </div>

            {/* Split Content */}
            <div className="relative z-10 flex-1 flex gap-4">
                <div className="flex-1 space-y-3 pt-2">
                    <SkeletonLine width="100%" className="bg-blue-900/10" />
                    <SkeletonLine width="85%" className="bg-blue-900/10" />
                    <SkeletonLine width="90%" className="bg-blue-900/10" />
                    <SkeletonLine width="60%" className="bg-blue-900/10" />
                </div>
                {/* Image Placeholder with Gradient */}
                <SkeletonRect className="w-20 h-full bg-gradient-to-b from-blue-100/40 to-blue-200/40 border border-white/30" />
            </div>

            {/* Doodle Decor */}
            <DoodleCircle className="absolute -bottom-8 -left-8 w-32 h-32 text-white/50" />
            <DoodleArrow className="absolute -top-10 -right-12 w-24 h-24 text-white/60 rotate-12" />
        </CardBase>
    );
};

// 2. Document/Report Skeleton
const DocumentSkeleton = ({ className = "" }: { className?: string }) => {
    return (
        <CardBase className={`w-56 h-64 p-6 flex flex-col gap-4 relative before:absolute before:top-0 before:right-0 before:w-12 before:h-12 before:bg-linear-to-bl before:from-white/40 before:to-transparent before:rounded-bl-2xl ${className}`}>
            {/* Header */}
            <div className="relative z-10 mb-2">
                <SkeletonLine width="50%" className="h-3 bg-blue-600/20 mb-3" />
                <SkeletonLine width="30%" className="bg-blue-400/20" />
            </div>

            {/* Body Text */}
            <div className="relative z-10 space-y-3">
                <SkeletonLine width="100%" className="bg-blue-900/10" />
                <SkeletonLine width="92%" className="bg-blue-900/10" />
                <SkeletonLine width="98%" className="bg-blue-900/10" />
            </div>

            {/* Bar Chart Visual */}
            <div className="relative z-10 mt-3 p-3 bg-white/20 rounded-xl border border-white/30 backdrop-blur-sm">
                <div className="flex items-end gap-2 h-12 justify-around px-1">
                    <div className="w-2.5 bg-blue-300/40 h-[40%] rounded-t-sm" />
                    <div className="w-2.5 bg-blue-500/40 h-[70%] rounded-t-sm" />
                    <div className="w-2.5 bg-blue-300/40 h-[50%] rounded-t-sm" />
                    <div className="w-2.5 bg-blue-500/40 h-[80%] rounded-t-sm" />
                </div>
            </div>

            {/* Random doodle */}
            <DoodleScribble className="absolute -bottom-8 left-10 w-32 h-8 text-white/60" />
        </CardBase>
    );
};

// 3. Captable / Data Skeleton
const CaptableSkeleton = ({ className = "" }: { className?: string }) => {
    return (
        <CardBase className={`w-80 h-52 p-0 flex flex-col ${className}`}>
            {/* Window Header */}
            <div className="h-8 border-b border-white/20 bg-white/10 px-4 flex items-center justify-between">
                <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400/80" />
                </div>
                <SkeletonLine width="60px" className="bg-white/20 h-1.5" />
            </div>

            <div className="flex flex-1 p-4 gap-4">
                {/* Donut Chart */}
                <div className="flex flex-col items-center justify-center gap-2 w-24 flex-shrink-0 border-r border-white/10 pr-2">
                    <div className="relative w-16 h-16 rounded-full border-[8px] border-blue-200/20 shadow-inner flex items-center justify-center">
                        <div className="absolute inset-0 rounded-full border-[8px] border-blue-500/40 border-t-transparent border-l-transparent rotate-45" />
                        <div className="w-8 h-8 rounded-full bg-blue-100/10" />
                    </div>
                    <SkeletonLine width="40px" className="bg-blue-300/30 h-1.5 mt-1" />
                </div>

                {/* Data Table */}
                <div className="flex-1 flex flex-col gap-2.5 justify-center">
                    {/* Table Header */}
                    <div className="flex justify-between items-center pb-1 border-b border-white/10 opacity-60">
                        <SkeletonLine width="40px" className="h-1.5" />
                        <SkeletonLine width="20px" className="h-1.5" />
                        <SkeletonLine width="20px" className="h-1.5" />
                    </div>

                    {/* Rows */}
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <div className={`w-1.5 h-1.5 rounded-sm ${i === 1 ? 'bg-blue-500/60' : 'bg-blue-300/40'}`} />
                                <SkeletonLine width={`${50 + Math.random() * 30}px`} className="bg-blue-800/20 h-2" />
                            </div>
                            <SkeletonLine width="24px" className="bg-blue-800/10 h-2" />
                        </div>
                    ))}
                </div>
            </div>
        </CardBase>
    );
};


// --- Main Composition Component ---
const SkeletonShowcase = () => {
    return (
        <div className="absolute inset-x-0 bottom-0 pointer-events-none z-[5] overflow-visible">
            <div className="max-w-9xl mx-auto relative h-[400px] w-full">

                {/* --- Left Group (Presentation Focus) --- */}
                <motion.div
                    className="absolute bottom-10 -left-4 md:-left-10 lg:left-0 scale-[0.6] md:scale-80 lg:scale-100 origin-bottom-left"
                    initial={{ x: -150, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                >
                    {/* Organic Line Decoration */}
                    <motion.div
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 2, delay: 0.5 }}
                        className="absolute -top-32 -right-32 text-white/30"
                    >
                        <svg width="400" height="200" viewBox="0 0 400 200" fill="none">
                            <path d="M10 150 Q 100 10, 200 100 T 380 50" stroke="currentColor" strokeWidth="4" strokeDasharray="12 12" />
                        </svg>
                    </motion.div>

                    {/* Big External Doodles - Left */}
                    <DoodleHighlight className="absolute -top-20 -left-10 w-64 h-16 text-blue-200/60 -rotate-12" />
                    <DoodleBurst className="absolute top-1/2 -right-32 w-32 h-32 text-white/50" />


                    {/* Back Card */}
                    <motion.div
                        animate={{ y: [0, -15, 0], rotate: [-8, -10, -8] }}
                        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute -top-16 left-12 z-0"
                    >
                        <DocumentSkeleton className="transform -rotate-6 brightness-110" />
                    </motion.div>

                    {/* Front Card */}
                    <motion.div
                        animate={{ y: [0, -20, 0], rotate: [4, 2, 4] }}
                        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                        className="relative z-10"
                    >
                        <PresentationSkeleton className="shadow-blue-500/10" />
                    </motion.div>
                </motion.div>


                {/* --- Right Group (Data Focus) --- */}
                <motion.div
                    className="absolute bottom-12 -right-8 md:-right-12 lg:-right-4 scale-[0.6] md:scale-80 lg:scale-100 origin-bottom-right"
                    initial={{ x: 150, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 1.2, ease: "easeOut", delay: 0.2 }}
                >
                    {/* Background Blob Decoration */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-blue-400/20 blur-[60px] rounded-full pointer-events-none" />

                    {/* Big External Doodles - Right */}
                    <DoodleCircle className="absolute -top-40 right-20 w-48 h-48 text-white/30 rotate-45" />
                    <DoodleArrow className="absolute -bottom-20 -left-32 w-48 h-48 text-blue-200/50 rotate-[100deg] scale-y-[-1]" />


                    {/* Back Card */}
                    <motion.div
                        animate={{ y: [0, -12, 0], rotate: [12, 10, 12] }}
                        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                        className="absolute -top-20 right-8 z-0"
                    >
                        <DocumentSkeleton className="transform rotate-12 brightness-110" />
                    </motion.div>

                    {/* Front Card */}
                    <motion.div
                        animate={{ y: [0, -25, 0], rotate: [-5, -3, -5] }}
                        transition={{ duration: 7.5, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                        className="relative z-10"
                    >
                        <CaptableSkeleton className="transform -rotate-3 shadow-blue-500/10" />
                    </motion.div>
                </motion.div>

            </div>
        </div>
    );
};

export default SkeletonShowcase;
